/**
 * Per-enemy-type tunable difficulty parameters.
 *
 * These values are intentionally exposed as plain data so the shared AI module
 * (src/game/enemyAI.ts) can consume them from both the main thread and a web
 * worker without pulling in React or any UI code.
 */
import type { WorldEnemyType } from '@/types/enemy';

export interface EnemyDifficultyConfig {
  /** 0-1 probability that the AI picks its optimal move vs a random one. */
  accuracy: number;
  /** Minimum turns before the enemy begins moving after spawn. */
  minActivationDelay: number;
  /** Additional random turns that may be added on top of minActivationDelay. */
  maxActivationDelayBonus: number;
  /** Visual stagger delay (ms) between consecutive enemies in a single turn. */
  staggerDelay: number;
  /** 0-1 chance the enemy skips its turn ("recalibration" pause). */
  skipTurnChance: number;

  // -- Patrol (Beetle) --
  /** 2 = back-and-forth, 4 = square patrol (rotate 90deg clockwise on wall hit). */
  patrolComplexity: 2 | 4;

  // -- Bounce (Mite) --
  /** 0-1 bias toward "interesting" positions (nearest collectible or player). */
  positionBias: number;

  // -- Stalk / Chase (Drone / Hound) --
  /** 0-1 chance to pick a random direction instead of the optimal one. */
  mistakeRate: number;

  // -- Vision & Chase --
  /** Length of forward vision cone in tiles (0 = no vision). */
  visionRange: number;
  /** Width of cone at far end (1 = single line, 3 = narrow spread, 5 = wide). */
  visionSpread: 1 | 3 | 5;
  /** Turns enemy chases after spotting player. */
  chaseDuration: number;
  /** How many slides per player turn (1 = normal, 2+ = multi-step). */
  movesPerTurn: number;
}

/**
 * Default difficulty config per enemy type.
 * Designers can override individual fields by spreading over these defaults.
 */
export const ENEMY_DIFFICULTY: Record<WorldEnemyType, EnemyDifficultyConfig> = {
  bramble_beetle: {
    accuracy: 1.0,
    minActivationDelay: 1,
    maxActivationDelayBonus: 0,
    staggerDelay: 120,
    skipTurnChance: 0,
    patrolComplexity: 4,
    positionBias: 0,
    mistakeRate: 0,
    visionRange: 0,
    visionSpread: 1,
    chaseDuration: 0,
    movesPerTurn: 1,
  },
  tunnel_drone: {
    accuracy: 0.85,
    minActivationDelay: 1,
    maxActivationDelayBonus: 1,
    staggerDelay: 120,
    skipTurnChance: 0.05,
    patrolComplexity: 2,
    positionBias: 0,
    mistakeRate: 0.15,
    visionRange: 4,
    visionSpread: 3,
    chaseDuration: 3,
    movesPerTurn: 1,
  },
  skid_mite: {
    accuracy: 0.6,
    minActivationDelay: 0,
    maxActivationDelayBonus: 1,
    staggerDelay: 100,
    skipTurnChance: 0.08,
    patrolComplexity: 2,
    positionBias: 0.35,
    mistakeRate: 0,
    visionRange: 0,
    visionSpread: 1,
    chaseDuration: 0,
    movesPerTurn: 2,
  },
  cinder_hound: {
    accuracy: 0.9,
    minActivationDelay: 2,
    maxActivationDelayBonus: 1,
    staggerDelay: 140,
    skipTurnChance: 0.03,
    patrolComplexity: 2,
    positionBias: 0,
    mistakeRate: 0.1,
    visionRange: 3,
    visionSpread: 5,
    chaseDuration: 5,
    movesPerTurn: 1,
  },
  echo_mirror: {
    accuracy: 1.0,
    minActivationDelay: 1,
    maxActivationDelayBonus: 0,
    staggerDelay: 120,
    skipTurnChance: 0,
    patrolComplexity: 2,
    positionBias: 0,
    mistakeRate: 0,
    visionRange: 0,
    visionSpread: 1,
    chaseDuration: 0,
    movesPerTurn: 1,
  },
};

// ---------------------------------------------------------------------------
// Timing constants consumed by the movement-stagger logic in useGameEngine
// ---------------------------------------------------------------------------

/** Base delay (ms) before the first enemy moves after a player step. */
export const ENEMY_BASE_DELAY = 250;

/** Additional delay (ms) between each successive enemy in a single turn. */
export const ENEMY_STAGGER = 120;
